/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import ImageBlock from '@component/common/block/image-block';
import TextButton from '@design-system/text-button';
import Canvas from '@design-system/canvas';
import Callout from '@design-system/callout';
import Gap from '@component/common/gap';
import { useTypedDispatch } from '@hook/common/useStore';
import { useAuthPage } from '@hook/auth/useAuth';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { actions as workbookActions } from '@store/slice/workbook-slice';
import { actions as classroomActions } from '@store/slice/classroom-slice';
import { createClozePath } from '@constant/iyagigo/route-path';
import { ISignupWallToastData } from '@datatype/iyagigo/type';
import { ETheme } from '@datatype/type';
import otterReadImage from '@asset/image/otter-read.png';

interface IProps {
  data: ISignupWallToastData['data'];
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
`;

const OtterMessageContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const FeatureContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const FEATURE_DESIGN = {
  text: { size: 'small', color: color.BLACK_2, bold: false },
  icon: { size: 'small', color: color.BROWN_1, kind: 'filled' },
  box: { alignX: 'left', gap: '6px' },
} as const;

const QUIT_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BROWN_1, bold: false, underline: true },
  box: { margin: `${size.MARGIN_1} 0px 0px 0px`, event: { cursor: 'pointer' } },
} as const;

const SigninRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const SignupWallToast: React.FC<IProps> = ({ data }) => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const { openAuthPage } = useAuthPage();
  const { trigger } = data;

  const handleSignupClick = () => {
    dispatch(modalActions.hideOverlay());
    openAuthPage('signin');
  };

  const handleQuitClick = () => {
    dispatch(modalActions.hideToast());
    // quota: 학습 종료 + today-home 이동 (한도 도달 → 더 못 풀이)
    // access: 닫기만 (사용자가 보던 화면 유지)
    if (trigger === 'quota') {
      dispatch(workbookActions.storeIsStudying(false));
      dispatch(classroomActions.storeIsStudying(false));
      navigate(createClozePath('home'));
    }
  };

  const calloutText =
    trigger === 'quota'
      ? '여기까지가 무료예요\n가입하고 이어서 풀어보세요'
      : '회원이 되면 쓸 수 있어요\n가입하고 무료로 써보세요';

  return (
    <Wrapper>
      <OtterMessageContainer>
        <ImageBlock src={otterReadImage} alt='이야기고' design={{ image: { height: '80px' } }} />
        <Callout text={calloutText} direction='left' theme={ETheme.CREAM} size='medium' width='fit-content' />
      </OtterMessageContainer>
      <Gap height={size.MARGIN_1} />
      <Canvas theme={ETheme.BROWN} shape='square' width='100%' padding={size.MARGIN_1}>
        <FeatureContainer>
          <IconTextBlock icon={'check-box'} text={'매일 학습 기록 쌓기'} design={FEATURE_DESIGN} />
          <IconTextBlock icon={'check-box'} text={'틀린 문제·단어 다시 풀기'} design={FEATURE_DESIGN} />
          <IconTextBlock icon={'check-box'} text={'어려웠던 문제·단어 저장하기'} design={FEATURE_DESIGN} />
          <IconTextBlock icon={'check-box'} text={'나의 학습통계 보기'} design={FEATURE_DESIGN} />
        </FeatureContainer>
      </Canvas>
      <Gap height={size.MARGIN_2} />
      <TextButton
        theme={ETheme.BROWN}
        kind={'filled'}
        size={'medium'}
        shape='pill'
        width={'100%'}
        text={'10초 만에 가입하기'}
        handleClick={handleSignupClick}
      />
      <Gap height={size.MARGIN_1} />
      <SigninRow>
        <TextBlock text={'이미 회원이세요?'} design={{ text: { size: 'medium', color: color.GRAY_1, bold: false } }} />
        <TextBlock
          text={'로그인하기'}
          design={{
            text: { size: 'medium', color: color.BROWN_1, bold: false, underline: true },
            box: { event: { cursor: 'pointer' } },
          }}
          click={{ handler: handleSignupClick }}
        />
      </SigninRow>
      {/*<TextBlock text={'다음에 할게요'} design={QUIT_TEXT_DESIGN} click={{ handler: handleQuitClick }} />*/}
    </Wrapper>
  );
};

export default SignupWallToast;
